import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useArticles } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Seo from "@/components/Seo";

/**
 * CARNET NANDA. Le terrain, les coulisses, les matières.
 * Pas une deuxième rubrique « news » : les lieux, les gestes, les rencontres
 * et les étapes des projets de NANDA SÁ, au Sénégal et en Guinée-Bissau.
 */
const RUBRIQUES = [
  { key: "all", fr: "Toutes", en: "All", pt: "Todas" },
  { key: "territoires", fr: "Territoires", en: "Territories", pt: "Territórios" },
  { key: "opportunites", fr: "Opportunités", en: "Opportunities", pt: "Oportunidades" },
  { key: "rencontres", fr: "Rencontres", en: "Encounters", pt: "Encontros" },
  { key: "savoir-faire", fr: "Savoir-faire", en: "Know-how", pt: "Saberes" },
  { key: "culture", fr: "Culture & Patrimoine", en: "Culture & Heritage", pt: "Cultura e Património" },
  { key: "voyage", fr: "Carnets de voyage", en: "Travel journals", pt: "Cadernos de viagem" },
  { key: "energie-innovation", fr: "Énergie & Innovation", en: "Energy & Innovation", pt: "Energia e Inovação" },
  { key: "entreprendre", fr: "Entreprendre", en: "Entrepreneurship", pt: "Empreender" },
  { key: "maison", fr: "Vie de NANDA SÁ", en: "NANDA SÁ Life", pt: "Vida NANDA SÁ" },
];

// Fallback mapping from existing category labels → rubrique keys
const CATEGORY_TO_RUBRIQUE = {
  // FR
  "Plantes & terroirs": "savoir-faire",
  "Énergie": "energie-innovation",
  "Architecture": "territoires",
  "Entreprise": "maison",
  "Portrait": "rencontres",
  "Territoire": "territoires",
  "Analyse": "opportunites",
  "Cuisine": "culture",
  // EN
  "Plants & terroirs": "savoir-faire",
  "Energy": "energie-innovation",
  "Enterprise": "maison",
  "Territory": "territoires",
  "Analysis": "opportunites",
  // PT
  "Plantas e terroirs": "savoir-faire",
  "Energia": "energie-innovation",
  "Arquitectura": "territoires",
  "Empresa": "maison",
  "Retrato": "rencontres",
  "Território": "territoires",
  "Análise": "opportunites",
};

const rubriqueOf = (article) =>
  article.rubrique || CATEGORY_TO_RUBRIQUE[article.category] || "maison";

export default function Carnet() {
  const ARTICLES = useArticles();
  const { locale } = useLanguage();
  const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);
  const dateLocale = locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR";
  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" }) : null;

  const [rubrique, setRubrique] = useState("all");
  const filtered =
    rubrique === "all" ? ARTICLES : ARTICLES.filter((a) => rubriqueOf(a) === rubrique);

  const [hero, ...rest] = filtered.length ? filtered : ARTICLES;

  return (
    <div data-testid="page-carnet" className="bg-white">
      <Seo path="/carnet" />

      {/* HERO */}
      <section className="border-b border-nanda-line">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-20 md:pb-28">
          <FadeUp>
            <div className="label-overline text-nanda-muted mb-8">{l("Le Carnet", "The Journal", "O Caderno")}</div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance max-w-4xl">
              {l("Le terrain, les coulisses, les matières.", "The field, the backstage, the materials.", "O terreno, os bastidores, as matérias.")}
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
              {l(
                "Le Carnet raconte la vie de NANDA SÁ au Sénégal et en Guinée-Bissau : les lieux, les gestes, les rencontres, les matériels et les étapes des projets. Des récits documentés, écrits depuis le terrain.",
                "The Journal tells the life of NANDA SÁ in Senegal and Guinea-Bissau: the places, the gestures, the encounters, the equipment and the steps of the projects. Documented stories, written from the field.",
                "O Caderno conta a vida da NANDA SÁ no Senegal e na Guiné-Bissau: os lugares, os gestos, os encontros, os materiais e as etapas dos projectos. Relatos documentados, escritos a partir do terreno."
              )}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Filtres rubriques */}
      <section className="border-b border-nanda-line sticky top-[64px] md:top-[80px] z-30 bg-white" data-testid="carnet-rubriques">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-4 overflow-x-auto">
          <div className="flex flex-nowrap items-center gap-2">
            {RUBRIQUES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRubrique(r.key)}
                data-testid={`rubrique-${r.key}`}
                className={`whitespace-nowrap text-xs tracking-[0.12em] uppercase font-medium px-4 py-2 rounded-full border transition-colors duration-300 ${
                  rubrique === r.key
                    ? "border-nanda-terracotta bg-nanda-terracotta text-white"
                    : "border-nanda-line text-nanda-inkSoft hover:border-nanda-ink hover:text-nanda-ink"
                }`}
              >
                {l(r.fr, r.en, r.pt)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* À la une */}
      {hero && (
        <section className="border-b border-nanda-line">
          <Link
            to={`/carnet/${hero.slug}`}
            data-testid={`carnet-feature-${hero.slug}`}
            className="group block hover:bg-nanda-bgAlt transition-colors duration-500"
          >
            <div className="mx-auto max-w-7xl px-6 sm:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
              <FadeUp>
                <div className="overflow-hidden rounded-3xl">
                  <img
                    src={hero.cover}
                    alt={hero.coverAlt || hero.title}
                    loading="eager"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  />
                </div>
              </FadeUp>
              <FadeUp delay={0.12}>
                <div className="label-overline text-nanda-terracotta mb-6">
                  {hero.category}{hero.date ? ` · ${formatDate(hero.date)}` : ""}
                </div>
                <h2 className="font-sans font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-nanda-ink text-balance">
                  {hero.title}
                </h2>
                <p className="mt-6 max-w-lg text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
                  {hero.excerpt}
                </p>
                <span className="mt-10 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline">
                  {l("Lire le récit", "Read the story", "Ler o relato")}
                  <ArrowUpRight size={15} strokeWidth={1.5} />
                </span>
              </FadeUp>
            </div>
          </Link>
        </section>
      )}

      {/* Grille */}
      <section className="border-b border-nanda-line">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28">
          {rest.length === 0 ? (
            <p className="text-nanda-muted text-center py-16 font-light">
              {l(
                "Cette rubrique s'enrichira prochainement. En attendant, explorez les autres.",
                "This section will grow soon. In the meantime, explore the others.",
                "Esta rubrica enriquecer-se-á em breve. Entretanto, explore as outras."
              )}
            </p>
          ) : (
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
              {rest.map((a) => (
                <StaggerItem key={a.slug}>
                  <Link
                    to={`/carnet/${a.slug}`}
                    data-testid={`carnet-article-${a.slug}`}
                    className="group block"
                  >
                    <div className="overflow-hidden rounded-3xl mb-6">
                      <img
                        src={a.cover}
                        alt={a.coverAlt || a.title}
                        loading="lazy"
                        className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="label-overline text-nanda-muted">
                      {a.category}{a.date ? ` · ${formatDate(a.date)}` : ""}
                    </div>
                    <h3 className="font-sans font-medium tracking-tight text-xl md:text-2xl mt-4 leading-tight text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-300">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-nanda-inkSoft text-sm md:text-base font-light leading-relaxed">{a.excerpt}</p>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>

      {/* Pont vers les Actualités */}
      <section className="bg-nanda-bgAlt">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <FadeUp className="md:col-span-8">
            <div className="label-overline text-nanda-muted mb-6">{l("Actualités", "News", "Actualidades")}</div>
            <h2 className="font-sans font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-nanda-ink text-balance max-w-2xl">
              {l("Ce qui se passe, au jour le jour.", "What is happening, day by day.", "O que se passa, dia a dia.")}
            </h2>
            <p className="mt-6 max-w-xl text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed">
              {l(
                "Lancements, arrivages, opérations et informations de la Maison : retrouvez les faits datés dans les Actualités.",
                "Launches, shipments, operations and House information: find the dated facts in the News.",
                "Lançamentos, chegadas, operações e informações da Casa: encontre os factos datados nas Actualidades."
              )}
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-4 flex md:justify-end">
            <Link
              to="/actualites"
              data-testid="carnet-cta-actualites"
              className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
            >
              {l("Voir les actualités", "See the news", "Ver as actualidades")}
              <ArrowUpRight size={15} strokeWidth={1.5} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
